/**
 * Payment API Client
 *
 * Handles payment session creation via Connect API integration with Ophelos.
 * The server creates the payment_sessions row for both case-level and
 * account-level checkouts and returns its id in the response.
 */

import { useAuth } from '@/contexts/customer-auth-context';
import {
  ConnectApiError,
  getConnectApiBaseUrl,
  previewDistributionOnConnect,
  type PaymentDistributionPreviewDto,
} from '@/lib/connect-api';

export interface PaymentPlanInstallment {
  subunits: number;
  date: string; // YYYY-MM-DD format
  time_zone: string; // IANA timezone (e.g., "Europe/Lisbon")
}

export interface CreatePaymentSessionRequest {
  // Either case_id (case-level, with payment_sessions row) or account_id
  // (account-level, no DB row) — exactly one must be set.
  case_id?: string;
  account_id?: string;
  amount_subunits?: number; // For immediate payments (full/partial)
  currency: string;
  payment_method: 'card' | 'sepa' | 'multibanco' | 'mb_way' | 'bacs';
  payment_type: 'full' | 'partial' | 'payment_plan';
  description: string;
  success_url: string;
  cancel_url: string;
  payment_plan_schedule?: PaymentPlanInstallment[]; // For payment plans
  payment_plan_frequency?: 'weekly' | 'biweekly' | 'monthly'; // For payment plans
  /** Discount offer to redeem at PSP checkout. Case-level full payments only (#3432). */
  discount_offer_id?: string;
}

export interface MultibancoReference {
  entity: string; // Multibanco entity code
  reference: string; // Payment reference number
  amount_subunits: number;
  currency: string;
  // ISO 8601. Absent on a legacy fresh mint, explicitly null when the shared
  // balance reference carries no expiry.
  expires_at?: string | null;
}

export interface CreatePaymentSessionResponse {
  checkout_url?: string; // For card/sepa - redirect to Ophelos checkout
  multibanco_reference?: MultibancoReference; // For multibanco - display reference
  payment_plan_id?: string; // For manual plans - external plan ID (pln_xxx)
  session_id?: string; // Forest payment session ID (psess_xxx)
  case_id?: string;
  account_id?: string;
  amount_subunits: number;
  currency: string;
  payment_method: string;
}

export class PaymentApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'PaymentApiError';
  }
}

/**
 * Payment API hook for creating Ophelos payment sessions
 *
 * Integrates with WorkOS authentication to retrieve JWT tokens
 * and make authenticated requests to the Connect API.
 *
 * @example
 * ```tsx
 * const { createPaymentSession, isCreating } = usePaymentApi();
 *
 * const handlePayment = async () => {
 *   try {
 *     const response = await createPaymentSession({
 *       case_id: 'case_123',
 *       amount_subunits: toSubunits(100.50),
 *       currency: 'EUR',
 *       payment_method: 'card',
 *       payment_type: 'full',
 *       description: 'Full payment',
 *       success_url: window.location.origin + '/payment/success',
 *       cancel_url: window.location.origin + '/payment/cancel',
 *     });
 *     window.location.href = response.checkout_url;
 *   } catch (error) {
 *     console.error('Payment failed:', error);
 *   }
 * };
 * ```
 */
export function usePaymentApi() {
  const { authStage, getAccessToken } = useAuth();

  /**
   * Create a payment session in Ophelos.
   *
   * The server creates the ``payment_sessions`` row (case-level or account-level),
   * appends its ``session_id`` to ``success_url`` so the redirect carries it, and
   * returns the id in the response. The client just sends the request body.
   *
   * @param request Payment session creation request
   * @returns Payment session response with checkout URL
   * @throws PaymentApiError if the request fails
   */
  const createPaymentSession = async (request: CreatePaymentSessionRequest): Promise<CreatePaymentSessionResponse> => {
    if (!request.case_id && !request.account_id) {
      throw new PaymentApiError('Either case_id or account_id must be provided.', 400);
    }
    if (request.case_id && request.account_id) {
      throw new PaymentApiError('Provide either case_id or account_id, not both.', 400);
    }

    let sessionId: string | undefined;

    try {
      // Get authentication credentials for Connect API
      let token: string | null = null;
      let accessCode: string | null = null;

      if (authStage === 'full') {
        // Stage 2: Get JWT token from WorkOS AuthKit
        token = await getAccessToken();
      } else if (authStage === 'limited') {
        // Stage 1: Use access code from localStorage
        accessCode = localStorage.getItem('access_code');
      }

      if (!token && !accessCode) {
        throw new PaymentApiError('No authentication found. Please log in again.', 401);
      }

      // Build headers based on available authentication
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (token) {
        // Stage 2: Use JWT Bearer token
        headers['Authorization'] = `Bearer ${token}`;
      } else if (accessCode) {
        // Stage 1: Use access code header
        headers['X-Access-Code'] = accessCode;
      }

      // Call the Connect API to create the Ophelos checkout.
      const response = await fetch(`${getConnectApiBaseUrl()}/connect/v1/payments/actions/create-checkout`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: response.statusText }));
        console.error('[createPaymentSession] Connect API error:', {
          case_id: request.case_id,
          account_id: request.account_id,
          status: response.status,
          error: errorData,
        });

        // Surface the server's error verbatim, preserving status (e.g. 409 when
        // an active arrangement or in-progress full payment blocks a new checkout)
        // so the page can branch on status without string-matching.
        // FastAPI returns errors in 'detail'; also check 'message' for compatibility.
        const errorMessage = errorData.detail || errorData.message || 'Payment processing failed. Please try again.';
        throw new PaymentApiError(errorMessage, response.status, errorData);
      }

      const responseData = await response.json();
      // The server creates the session for both scopes and returns its id.
      sessionId = responseData.session_id;
      console.log('[createPaymentSession] Success:', {
        session_id: sessionId,
        case_id: request.case_id,
        has_checkout_url: !!responseData.checkout_url,
        has_multibanco_reference: !!responseData.multibanco_reference,
      });

      return responseData;
    } catch (error) {
      if (error instanceof PaymentApiError) {
        throw error;
      }

      // Network/timeout errors before the server responded. The server owns
      // session cleanup (sweep_expired_payment_sessions), so there is nothing
      // to roll back client-side.
      console.error('[createPaymentSession] Unexpected error:', {
        case_id: request.case_id,
        payment_type: request.payment_type,
        error,
      });

      throw new PaymentApiError(
        'Failed to create payment session. Please check your connection and try again.',
        503,
        error
      );
    }
  };

  /**
   * Cancel a payment plan in Ophelos
   *
   * @param paymentPlanId External payment plan ID (pln_xxx from Ophelos)
   * @returns Cancellation confirmation response
   * @throws PaymentApiError if the request fails
   */
  const cancelPaymentPlan = async (paymentPlanId: string): Promise<{ message: string }> => {
    try {
      // Check authentication stage and get appropriate credentials
      let token: string | null = null;
      let accessCode: string | null = null;

      if (authStage === 'full') {
        // Stage 2: Get JWT token from WorkOS AuthKit
        token = await getAccessToken();
      } else if (authStage === 'limited') {
        // Stage 1: Use access code from localStorage
        accessCode = localStorage.getItem('access_code');
      }

      if (!token && !accessCode) {
        throw new PaymentApiError('No authentication found. Please log in again.', 401);
      }

      // Build headers based on available authentication
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (token) {
        // Stage 2: Use JWT Bearer token
        headers['Authorization'] = `Bearer ${token}`;
      } else if (accessCode) {
        // Stage 1: Use access code header
        headers['X-Access-Code'] = accessCode;
      }

      const response = await fetch(`${getConnectApiBaseUrl()}/connect/v1/payment-plans/${paymentPlanId}/actions/cancel`, {
        method: 'POST',
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: response.statusText }));
        // FastAPI returns errors in 'detail' field, but also check 'message' for compatibility
        const errorMessage = errorData.detail || errorData.message || `Payment API error: ${response.status}`;
        throw new PaymentApiError(errorMessage, response.status, errorData);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof PaymentApiError) {
        throw error;
      }
      throw new PaymentApiError(`Failed to cancel payment plan: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  /**
   * Preview how an account-level payment would split across its cases.
   *
   * Read-only: creates no session/plan. The backend ledger allocator owns the
   * split strategy, so the caller renders the returned distribution rather than
   * re-deriving it. ``case_id`` maps to a debt via ``DebtData.id``.
   */
  const previewDistribution = async ({
    accountId,
    amountSubunits,
    currency,
  }: {
    accountId: string;
    amountSubunits: number;
    currency: string;
  }): Promise<PaymentDistributionPreviewDto> => {
    const authStageOrNull = authStage === 'full' || authStage === 'limited' ? authStage : null;
    try {
      return await previewDistributionOnConnect({ accountId, amountSubunits, currency }, authStageOrNull, getAccessToken);
    } catch (error) {
      if (error instanceof ConnectApiError) {
        throw new PaymentApiError(error.message, error.status, error);
      }
      throw new PaymentApiError('Failed to preview payment distribution.', 503, error);
    }
  };

  return {
    createPaymentSession,
    cancelPaymentPlan,
    previewDistribution,
  };
}
